import { ChevronLeft, Check, CreditCard } from "lucide-react";
import { Button } from "@/app/components/button";
import { CheckoutProgress } from "@/app/components/checkout-progress";
import { useState } from "react";
import { formatCurrency } from "@/config/locale";

interface CheckoutInstallmentsPageProps {
  onBack: () => void;
  totalAmount: number;
  onContinue: (installments: number) => void;
}

export function CheckoutInstallmentsPage({
  onBack,
  totalAmount,
  onContinue,
}: CheckoutInstallmentsPageProps) {
  const [selectedInstallments, setSelectedInstallments] = useState(1);

  // Calculate installment options (1 to 12)
  const installmentOptions = Array.from({ length: 12 }, (_, i) => {
    const installmentNumber = i + 1;
    
    // Brazilian standard: interest-free up to 3x, then 1.5% per installment above 3
    let interestRate = 0;
    let hasInterest = false;
    
    if (installmentNumber > 3) {
      // Interest starts from 4x onwards: (installmentNumber - 3) * 1.5%
      interestRate = (installmentNumber - 3) * 0.015;
      hasInterest = true;
    }
    
    const totalWithInterest = totalAmount * (1 + interestRate);
    const installmentWithInterest = totalWithInterest / installmentNumber;
    
    return {
      number: installmentNumber,
      amount: installmentWithInterest,
      total: totalWithInterest,
      hasInterest,
      interestRate: interestRate * 100,
    };
  });

  return (
    <div className="min-h-screen bg-background pb-8">
      {/* Header */}
      <header className="bg-primary border-b border-primary sticky top-0 z-40 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center gap-4">
            <button
              onClick={onBack}
              className="p-2 hover:bg-white/10 rounded-lg transition-colors"
            >
              <ChevronLeft className="w-6 h-6 text-white" />
            </button>
            <div>
              <h1 className="text-xl text-white">Parcelamento</h1>
              <p className="text-sm text-white/80">Escolha o número de parcelas</p>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Progress Indicator */}
        <CheckoutProgress currentStep={3} stepLabel="Pagamento" />

        {/* Info Card */}
        <div className="bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-900 rounded-lg p-4 mb-6">
          <div className="flex gap-3">
            <CreditCard className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm text-blue-900 dark:text-blue-100 font-medium mb-1">
                Parcelamento no Cartão de Crédito
              </p>
              <p className="text-xs text-blue-700 dark:text-blue-300">
                Valor total: {formatCurrency(totalAmount)} • Parcelas sem juros até 3x
              </p>
            </div>
          </div>
        </div>

        {/* Installment Options */}
        <div className="space-y-3 mb-6">
          {installmentOptions.map((option) => (
            <button
              key={option.number}
              onClick={() => setSelectedInstallments(option.number)}
              className={`w-full p-4 border-2 rounded-lg text-left transition-all ${
                selectedInstallments === option.number
                  ? "border-primary bg-primary/5 shadow-sm"
                  : "border-border hover:border-primary/50"
              }`}
            >
              <div className="flex items-start gap-3">
                {/* Selection Radio */}
                <div
                  className={`mt-1 w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                    selectedInstallments === option.number
                      ? "border-primary bg-primary"
                      : "border-border"
                  }`}
                >
                  {selectedInstallments === option.number && (
                    <Check className="w-3 h-3 text-white" />
                  )}
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2 mb-1">
                    <div className="flex-1">
                      <h3 className="font-semibold text-base">
                        {option.number}x de {formatCurrency(option.amount)}
                      </h3>
                      {option.number === 1 ? (
                        <p className="text-sm text-muted-foreground">
                          À vista • Total: {formatCurrency(option.total)}
                        </p>
                      ) : (
                        <p className="text-sm text-muted-foreground">
                          Total: {formatCurrency(option.total)}
                          {option.hasInterest && option.number > 3 && (
                            <span className="text-orange-600 dark:text-orange-400 ml-1">
                              (com juros de {option.interestRate.toFixed(1)}%)
                            </span>
                          )}
                        </p>
                      )}
                    </div>
                    
                    {/* Badge */}
                    {option.number <= 3 && (
                      <span className="text-xs bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 px-2 py-1 rounded-full font-medium flex-shrink-0">
                        Sem juros
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </button>
          ))}
        </div>

        {/* Continue Button */}
        <Button
          onClick={() => onContinue(selectedInstallments)}
          className="w-full"
          size="lg"
        >
          Continuar para Revisão
        </Button>

        {/* Help Text */}
        <p className="text-xs text-center text-muted-foreground mt-4">
          {selectedInstallments <= 3 
            ? "Parcelamento sem juros" 
            : "Parcelamento com juros aplicados"
          }
        </p>
      </div>
    </div>
  );
}